import React, { useState } from 'react';
import {
  X,
  Calendar,
  MapPin,
  Home,
  Edit2,
  Trash2,
  Plus,
  FileText,
  Target,
  ArrowLeft,
} from 'react-feather';
import { tournamentAPI, Tournament, TournamentGame } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import AddEditTournamentModal from './AddEditTournamentModal';
import CoachesAttendingSection from './CoachesAttendingSection';
import AddEditGameModal from './AddEditGameModal';
import { useResponsive } from '../hooks/useResponsive';
import { fieldLabel } from '../utils/fieldLabel';

interface TournamentDetailModalProps {
  tournament: Tournament;
  onClose: () => void;
  onUpdate: () => Promise<void>;
}


const TournamentDetailModal: React.FC<TournamentDetailModalProps> = ({
  tournament,
  onClose,
  onUpdate,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [deleting, setDeleting] = useState(false);
  const [showEditTournamentModal, setShowEditTournamentModal] = useState(false);
  const [showAddEditGameModal, setShowAddEditGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState<TournamentGame | null>(null);

  const { ConfirmComponent: DeleteTournamentConfirm, confirm: confirmDeleteTournament } = useConfirm({
    title: 'Delete Tournament',
    message: `Are you sure you want to delete "${tournament.name}"? This will also delete all games.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  });

  const { ConfirmComponent: DeleteGameConfirm, confirm: confirmDeleteGame } = useConfirm({
    title: 'Delete Game',
    message: 'Are you sure you want to delete this game?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  });

  const handleDeleteTournament = async () => {
    if (!sessionToken) return;

    const confirmed = await confirmDeleteTournament();
    if (!confirmed) return;

    setDeleting(true);
    try {
      const response = await tournamentAPI.deleteTournament(sessionToken, tournament.id);
      if (response.success) {
        toast.success('Tournament deleted successfully');
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete tournament');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteGame = async (game: TournamentGame) => {
    if (!sessionToken) return;

    const confirmed = await confirmDeleteGame();
    if (!confirmed) return;

    try {
      const response = await tournamentAPI.deleteGame(sessionToken, game.id);
      if (response.success) {
        toast.success('Game deleted successfully');
        onUpdate();
      }
    } catch (error) {
      console.error('Delete game error:', error);
      toast.error('Failed to delete game');
    }
  };

  const handleAddGame = () => {
    setEditingGame(null);
    setShowAddEditGameModal(true);
  };

  const handleEditGame = (game: TournamentGame) => {
    setEditingGame(game);
    setShowAddEditGameModal(true);
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate.split('T')[0] + 'T00:00:00');
    const end = new Date(endDate.split('T')[0] + 'T00:00:00');

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    }

    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const sortedGames =
    tournament.games?.sort((a, b) => {
      return new Date(a.game_date.split('T')[0] + 'T00:00:00').getTime() - new Date(b.game_date.split('T')[0] + 'T00:00:00').getTime();
    }) || [];

  return (
    <>
      <DeleteTournamentConfirm />
      <DeleteGameConfirm />
      
      <div style={styles.overlay} onClick={onClose}>
        <div style={{
          ...styles.modal,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '800px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
        }} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{
            ...styles.header,
            padding: isMobile ? '16px' : '20px 24px',
          }}>
            <button onClick={onClose} style={styles.backButton}>
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div style={styles.content}>
            {/* Tournament Info Card */}
            <div style={styles.tournamentCard}>
              <div style={styles.tournamentHeader}>
                <h2 style={styles.tournamentName}>{tournament.name}</h2>
                <div style={styles.actionButtons}>
                  <button
                    onClick={() => setShowEditTournamentModal(true)}
                    style={styles.editButton}
                    title="Edit Tournament"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={handleDeleteTournament}
                    disabled={deleting}
                    style={styles.deleteButton}
                    title="Delete Tournament"
                  >
                    {deleting ? (
                      <div style={styles.smallSpinner} />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.detailSection}>
                <div style={styles.detailRow}>
                  <Calendar size={16} style={styles.icon} />
                  <span style={styles.detailLabel}>Dates:</span>
                  <span style={styles.detailValue}>
                    {formatDateRange(tournament.start_date, tournament.end_date)}
                  </span>
                </div>

                {tournament.facility_name && (
                  <div style={styles.detailRow}>
                    <Home size={16} style={styles.icon} />
                    <span style={styles.detailLabel}>Facility:</span>
                    <span style={styles.detailValue}>{tournament.facility_name}</span>
                  </div>
                )}

                <div style={styles.detailRow}>
                  <MapPin size={16} style={styles.icon} />
                  <span style={styles.detailLabel}>Location:</span>
                  <span style={styles.detailValue}>
                    {tournament.city}, {tournament.state}
                  </span>
                </div>

                {tournament.notes && (
                  <div style={styles.notesSection}>
                    <div style={styles.notesHeader}>
                      <FileText size={16} style={styles.icon} />
                      <span style={styles.detailLabel}>Notes:</span>
                    </div>
                    <p style={styles.notesText}>{tournament.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* KGH-795 / APS-255 item 11. Above the games on purpose: knowing a
                program will be here is what makes a family bother to fill the
                game times in, and burying it under the schedule would invert
                that. The games go down with it because the section's whole verb
                is sending them, and its "no games yet" state hands the family
                straight to the same Add Game modal the section below opens. */}
            <CoachesAttendingSection
              tournamentId={tournament.id}
              athleteProfileId={tournament.athlete_profile_id}
              games={sortedGames}
              onAddGame={handleAddGame}
            />

            {/* Games Section */}
            <div style={styles.gamesSection}>
              <div style={styles.gamesSectionHeader}>
                <div style={styles.gamesHeaderLeft}>
                  <Target size={20} style={{ color: '#F9550D' }} />
                  <h3 style={styles.gamesTitle}>
                    Games ({sortedGames.length})
                  </h3>
                </div>
                <button onClick={handleAddGame} style={styles.addGameButton}>
                  <Plus size={18} />
                  Add Game
                </button>
              </div>

              {sortedGames.length === 0 ? (
                <div style={styles.emptyGames}>
                  <Target size={48} color="#D1D5DB" />
                  <p style={styles.emptyGamesText}>No games added yet</p>
                  <button onClick={handleAddGame} style={styles.addGameButtonLarge}>
                    <Plus size={20} />
                    Add First Game
                  </button>
                </div>
              ) : (
                <div style={styles.gamesList}>
                  {sortedGames.map((game) => (
                    <div key={game.id} style={styles.gameCard}>
                      <div style={styles.gameHeader}>
                        <div>
                          <h4 style={styles.opponent}>vs {game.opponent}</h4>
                          <div style={styles.gameDateTime}>
                            <Calendar size={14} style={styles.gameIcon} />
                            <span style={styles.gameInfoText}>
                              {new Date(game.game_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                            {game.game_time && (
                              <>
                                <span style={styles.separator}>•</span>
                                <span style={styles.gameInfoText}>
                                  {new Date(`2000-01-01T${game.game_time}`).toLocaleTimeString(
                                    'en-US',
                                    {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true,
                                    }
                                  )}
                                </span>
                              </>
                            )}
                          </div>
                          {fieldLabel(game.field_number) && (
                            <div style={styles.gameField}>
                              <Home size={14} style={styles.gameIcon} />
                              <span style={styles.gameInfoText}>{fieldLabel(game.field_number)}</span>
                            </div>
                          )}
                        </div>
                        <div style={styles.gameActions}>
                          <button
                            onClick={() => handleEditGame(game)}
                            style={styles.gameEditButton}
                            title="Edit Game"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteGame(game)}
                            style={styles.gameDeleteButton}
                            title="Delete Game"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showEditTournamentModal && (
        <AddEditTournamentModal
          athleteId={tournament.athlete_profile_id}
          tournament={tournament}
          onClose={() => setShowEditTournamentModal(false)}
          onSuccess={onUpdate}
        />
      )}

      {showAddEditGameModal && (
        <AddEditGameModal
          tournamentId={tournament.id}
          game={editingGame}
          onClose={() => {
            setShowAddEditGameModal(false);
            setEditingGame(null);
          }}
          onSuccess={onUpdate}
        />
      )}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #E5E7EB',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 12px',
    color: '#F9550D',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  tournamentCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  tournamentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  tournamentName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
    flex: 1,
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    color: '#F9550D',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButton: {
    padding: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    color: '#EF4444',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  smallSpinner: {
    width: '18px',
    height: '18px',
    border: '2px solid #FEE2E2',
    borderTop: '2px solid #EF4444',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    color: '#6B7280',
    flexShrink: 0,
  },
  detailLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6B7280',
    fontFamily: 'Manrope, sans-serif',
    minWidth: '80px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#1F2937',
    fontFamily: 'Manrope, sans-serif',
  },
  notesSection: {
    marginTop: '8px',
  },
  notesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  notesText: {
    fontSize: '14px',
    color: '#1F2937',
    fontFamily: 'Manrope, sans-serif',
    margin: 0,
    paddingLeft: '24px',
    lineHeight: '1.6',
  },
  gamesSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    padding: '20px',
  },
  gamesSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  gamesHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  gamesTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
  },
  addGameButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  emptyGames: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyGamesText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '16px 0 24px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  addGameButtonLarge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  gamesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  gameCard: {
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
  },
  gameHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  opponent: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1F2937',
    margin: '0 0 8px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  gameDateTime: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  gameField: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  gameIcon: {
    color: '#6B7280',
    flexShrink: 0,
  },
  gameInfoText: {
    fontSize: '14px',
    color: '#6B7280',
    fontFamily: 'Manrope, sans-serif',
  },
  separator: {
    color: '#D1D5DB',
    fontSize: '14px',
  },
  gameActions: {
    display: 'flex',
    gap: '8px',
  },
  gameEditButton: {
    padding: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    color: '#F9550D',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  gameDeleteButton: {
    padding: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    color: '#EF4444',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
};

export default TournamentDetailModal;
